import { FC } from 'react';
import { Card, CardBody, CardHeader } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { FiMapPin, FiGlobe, FiUsers, FiCoffee } from 'react-icons/fi';
import { ROUTES } from '~/constants/routes.contants';
import { BaseLink } from '~/components/atoms/Link';
import { useEvent } from '~/hooks/useEvent';

interface EndOfScrollProps {
  currentPageType?: 'food' | 'place' | 'user' | 'feed';
}

export const EndOfScroll: FC<EndOfScrollProps> = ({ currentPageType = 'food' }) => {
  const { t } = useTranslation();
  const { sendEvent } = useEvent();

  const handleLinkClick = (destination: string) => {
    sendEvent('end_of_scroll_navigation', {
      from: currentPageType,
      to: destination
    });
  };

  return (
    <div className="w-full p-8 pb-32">
      <div className="flex flex-col items-center text-center mb-6">
        <h3 className="text-xl font-bold text-warning-600">{t('common.exploreMore')}</h3>
        <p className="text-sm text-secondary-600">{t('common.discoverExperiences')}</p>
      </div>

      <div className="bg-background shadow-sm rounded-xl p-6 max-w-3xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {currentPageType !== 'place' && (
            <BaseLink href={ROUTES.PLACES} className="block w-full">
              <Card className="hover:shadow-lg transition-shadow w-full h-full bg-gradient-to-br from-warning-50 to-warning-100 border-warning-200 border" onPress={() => handleLinkClick('places_near_me')}>
                <CardHeader className="flex justify-center pb-0">
                  <FiMapPin className="text-3xl text-warning-600" />
                </CardHeader>
                <CardBody className="flex flex-col items-center text-center pb-5">
                  <h4 className="text-lg font-medium text-warning-700">{t('endOfScroll.placesNearMe')}</h4>
                  <p className="text-sm text-warning-600">{t('endOfScroll.placesNearMeSubtitle')}</p>
                </CardBody>
              </Card>
            </BaseLink>
          )}

          {currentPageType !== 'feed' && (
            <BaseLink href={ROUTES.FEED} className="block w-full">
              <Card className="hover:shadow-lg transition-shadow w-full h-full bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200 border" onPress={() => handleLinkClick('global_feed')}>
                <CardHeader className="flex justify-center pb-0">
                  <FiGlobe className="text-3xl text-primary-600" />
                </CardHeader>
                <CardBody className="flex flex-col items-center text-center pb-5">
                  <h4 className="text-lg font-medium text-primary-700">{t('endOfScroll.globalFeed')}</h4>
                  <p className="text-sm text-primary-600">{t('endOfScroll.globalFeedSubtitle')}</p>
                </CardBody>
              </Card>
            </BaseLink>
          )}

          {currentPageType !== 'user' && (
            <BaseLink href={ROUTES.USERS} className="block w-full">
              <Card className="hover:shadow-lg transition-shadow w-full h-full bg-gradient-to-br from-secondary-50 to-secondary-100 border-secondary-200 border" onPress={() => handleLinkClick('find_people')}>
                <CardHeader className="flex justify-center pb-0">
                  <FiUsers className="text-3xl text-secondary-600" />
                </CardHeader>
                <CardBody className="flex flex-col items-center text-center pb-5">
                  <h4 className="text-lg font-medium text-secondary-700">{t('endOfScroll.findPeople')}</h4>
                  <p className="text-sm text-secondary-600">{t('endOfScroll.findPeopleSubtitle')}</p>
                </CardBody>
              </Card>
            </BaseLink>
          )}

          {currentPageType !== 'food' && (
            <BaseLink href={ROUTES.FOODS} className="block w-full">
              <Card className="hover:shadow-lg transition-shadow w-full h-full bg-gradient-to-br from-danger-50 to-danger-100 border-danger-200 border" onPress={() => handleLinkClick('foods_near_me')}>
                <CardHeader className="flex justify-center pb-0">
                  <FiCoffee className="text-3xl text-danger-600" />
                </CardHeader>
                <CardBody className="flex flex-col items-center text-center pb-5">
                  <h4 className="text-lg font-medium text-danger-700">{t('endOfScroll.foodsNearMe')}</h4>
                  <p className="text-sm text-danger-600">{t('endOfScroll.foodsNearMeSubtitle')}</p>
                </CardBody>
              </Card>
            </BaseLink>
          )}
        </div>
      </div>

      <div className="text-center mt-6 text-xs text-secondary-400">
        {t('common.reachedEndOfContent')}
      </div>
    </div>
  );
}; 